import React, { useEffect, useState, useCallback } from 'react';
import Component from '../../components/Component';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, Chip, Divider,
    FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import CollectionsIcon from '@mui/icons-material/Collections';
import {
    listDigestDays, getDigestDay, updateDigestCard, renderDigestDay, regenerateDigestDay, uploadDigestImage,
} from '../../services/morningBriefStudioApi';

// One editable player row (photo cards): avatar preview + name/team/stat + image
// URL, plus an upload button to replace the headshot/team image.
function PlayerRow({ player, onChange }) {
    const [uploading, setUploading] = useState(false);
    const set = (k, v) => onChange({ ...player, [k]: v });
    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try { const url = await uploadDigestImage(file); onChange({ ...player, avatar_url: url }); }
        catch (err) { alert(err.message); } finally { setUploading(false); }
    };
    return (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
            <Box sx={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                {player.avatar_url
                    ? <img src={player.avatar_url} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ccc' }} />
                    : <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(player.name || '?').slice(0, 2).toUpperCase()}</Box>}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField label="Name" size="small" value={player.name || ''} onChange={(e) => set('name', e.target.value)} sx={{ flex: 1 }} />
                    <TextField label="Team" size="small" value={player.team || ''} onChange={(e) => set('team', e.target.value)} sx={{ width: 90 }} />
                    <TextField label="Stat" size="small" value={player.stat_line || ''} onChange={(e) => set('stat_line', e.target.value)} sx={{ flex: 1 }} />
                </Box>
                <TextField label="Image URL" size="small" value={player.avatar_url || ''} onChange={(e) => set('avatar_url', e.target.value)} fullWidth />
            </Box>
            <Button component="label" size="small" variant="outlined" disabled={uploading} sx={{ flexShrink: 0 }}>
                {uploading ? <CircularProgress size={16} /> : 'Upload'}
                <input hidden type="file" accept="image/*" onChange={upload} />
            </Button>
        </Box>
    );
}

const todayKey = () => new Date().toISOString().slice(0, 10);

// One editable story-card row: text editor on the left, rendered preview on the right.
function CardEditor({ dayKey, card, onSaved }) {
    const [title, setTitle] = useState(card.title || '');
    const [boxText, setBoxText] = useState(card.box_text || '');
    const [imageUrl, setImageUrl] = useState(card.image_url || '');
    const [players, setPlayers] = useState(card.players || []);
    const [busy, setBusy] = useState(null); // 'save' | 'render'
    const [err, setErr] = useState('');

    useEffect(() => {
        setTitle(card.title || ''); setBoxText(card.box_text || ''); setImageUrl(card.image_url || ''); setPlayers(card.players || []);
    }, [card.id, card.title, card.box_text, card.image_url, card.players]);

    const playersDirty = JSON.stringify(players) !== JSON.stringify(card.players || []);
    const dirty = title !== (card.title || '') || boxText !== (card.box_text || '') || imageUrl !== (card.image_url || '') || playersDirty;
    const hasPlayers = Array.isArray(card.players) && card.players.length > 0;

    const patch = () => {
        const p = { title, box_text: boxText, image_url: imageUrl || null };
        if (hasPlayers) p.players = players;
        return p;
    };

    const save = async () => {
        setBusy('save'); setErr('');
        try {
            const r = await updateDigestCard(dayKey, card.id, patch());
            onSaved(card.id, r?.data?.card);
        } catch (e) { setErr(e.message); } finally { setBusy(null); }
    };

    const render = async () => {
        setBusy('render'); setErr('');
        try {
            // persist text + player edits first so the rendered image reflects them
            if (dirty) await updateDigestCard(dayKey, card.id, patch());
            const r = await renderDigestDay(dayKey, card.id);
            const item = (r?.data?.rendered || []).find((x) => x.id === card.id);
            if (item?.error) throw new Error(item.error);
            // reload the card to pick up the fresh image_url
            const day = await getDigestDay(dayKey);
            const fresh = (day?.data?.cards || []).find((c) => c.id === card.id);
            if (fresh) { setImageUrl(fresh.image_url || ''); onSaved(card.id, fresh); }
        } catch (e) { setErr(e.message); } finally { setBusy(null); }
    };

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip size="small" label={`#${card.id}`} />
                    {card.label && <Chip size="small" variant="outlined" label={card.label} />}
                    {card.image_url && <Chip size="small" color="success" variant="outlined" icon={<ImageIcon />} label="rendered" />}
                    {Array.isArray(card.image_pages) && card.image_pages.length > 1 &&
                        <Chip size="small" color="info" variant="outlined" label={`${card.image_pages.length} pages`} />}
                    {card.edited_at && <Chip size="small" color="warning" variant="outlined" label="edited" />}
                </Stack>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* editor */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField label="Title" fullWidth size="small" value={title}
                            onChange={(e) => setTitle(e.target.value)} sx={{ mb: 1.5 }} />
                        <TextField label="Body (markdown)" fullWidth multiline minRows={6} size="small"
                            value={boxText} onChange={(e) => setBoxText(e.target.value)} sx={{ mb: 1.5 }} />
                        <TextField label="Image URL (auto-filled by Render; or paste your own)" fullWidth size="small"
                            value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} sx={{ mb: 1.5 }} />
                        {hasPlayers && (
                            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Players — edit name / team / stat, replace headshot or team image</Typography>
                                {players.map((p, i) => (
                                    <PlayerRow key={i} player={p} onChange={(np) => setPlayers((prev) => prev.map((x, xi) => (xi === i ? np : x)))} />
                                ))}
                            </Box>
                        )}
                        {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" startIcon={busy === 'save' ? <CircularProgress size={16} /> : <SaveIcon />}
                                disabled={!!busy || !dirty} onClick={save}>Save</Button>
                            <Button variant="outlined" size="small" startIcon={busy === 'render' ? <CircularProgress size={16} /> : <ImageIcon />}
                                disabled={!!busy} onClick={render}>Render image</Button>
                        </Stack>
                    </Box>
                    {/* preview */}
                    <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0 }}>
                        {imageUrl ? (
                            <a href={imageUrl} target="_blank" rel="noreferrer">
                                <img src={imageUrl} alt="card preview" style={{ width: '100%', borderRadius: 12, aspectRatio: '9 / 16', objectFit: 'cover', border: '1px solid #ddd' }} />
                            </a>
                        ) : card.rendered_at ? (
                            <Box sx={{ width: '100%', aspectRatio: '9 / 16', borderRadius: 1.5, border: '1px dashed #bbb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', p: 2, textAlign: 'center' }}>
                                <Stack alignItems="center" spacing={0.5}>
                                    <ImageIcon />
                                    <Typography variant="caption">Text-only card</Typography>
                                    <Typography variant="caption" color="text.disabled">Title & body render as native text in-app — no card image needed</Typography>
                                </Stack>
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%', aspectRatio: '9 / 16', borderRadius: 1.5, border: '1px dashed #bbb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                <Stack alignItems="center"><ImageIcon /><Typography variant="caption">No image yet</Typography></Stack>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function MorningBriefStudio() {
    const [days, setDays] = useState([]);
    const [dayKey, setDayKey] = useState('');
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null); // 'regen' | 'renderAll'
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const loadDays = useCallback(async () => {
        try {
            const r = await listDigestDays(60);
            const list = r?.data?.days || [];
            setDays(list);
            if (!dayKey && list.length) setDayKey(list[0].day_key);
            else if (!dayKey) setDayKey(todayKey());
        } catch (e) { setErr(e.message); }
    }, [dayKey]);

    const loadDay = useCallback(async (dk) => {
        if (!dk) return;
        setLoading(true); setErr(''); setMsg('');
        try {
            const r = await getDigestDay(dk);
            setCards(r?.data?.cards || []);
        } catch (e) { setCards([]); setErr(e.message); } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadDays(); }, [loadDays]);
    useEffect(() => { if (dayKey) loadDay(dayKey); }, [dayKey, loadDay]);

    const onCardSaved = (id, fresh) => {
        if (!fresh) return;
        setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...fresh } : c)));
    };

    const regenerate = async () => {
        setBusy('regen'); setErr(''); setMsg('');
        try {
            const r = await regenerateDigestDay(dayKey);
            setMsg(`Regenerated in ${r?.data?.generated_in_ms}ms — ${r?.data?.card_count} cards`);
            await loadDay(dayKey); await loadDays();
        } catch (e) { setErr(e.message); } finally { setBusy(null); }
    };

    const renderAll = async () => {
        setBusy('renderAll'); setErr(''); setMsg('');
        try {
            const r = await renderDigestDay(dayKey);
            const ok = (r?.data?.rendered || []).filter((x) => x.image_url).length;
            const failed = (r?.data?.rendered || []).filter((x) => x.error).length;
            setMsg(`Rendered ${ok} card image(s)${failed ? `, ${failed} failed` : ''} in ${r?.data?.ms}ms`);
            await loadDay(dayKey);
        } catch (e) { setErr(e.message); } finally { setBusy(null); }
    };

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <Box sx={{ py: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <CollectionsIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Daily Digest Studio</Typography>
                    <Box sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Day</InputLabel>
                        <Select label="Day" value={dayKey} onChange={(e) => setDayKey(e.target.value)}>
                            {days.map((d) => (
                                <MenuItem key={d.day_key} value={d.day_key}>
                                    {d.day_key}{d.day_key === todayKey() ? ' (today)' : ''} · {d.card_count || 0} cards{d.rendered_count ? ` · ${d.rendered_count} rendered` : ''}
                                </MenuItem>
                            ))}
                            {!days.some((d) => d.day_key === todayKey()) && <MenuItem value={todayKey()}>{todayKey()} (today)</MenuItem>}
                        </Select>
                    </FormControl>
                    <Tooltip title="Re-render all card images for this day">
                        <span><Button variant="outlined" startIcon={busy === 'renderAll' ? <CircularProgress size={16} /> : <ImageIcon />}
                            disabled={!!busy || !cards.length} onClick={renderAll}>Render all</Button></span>
                    </Tooltip>
                    <Tooltip title="Regenerate content from fresh match data (today only)">
                        <span><Button variant="contained" color="secondary" startIcon={busy === 'regen' ? <CircularProgress size={16} /> : <AutorenewIcon />}
                            disabled={!!busy || dayKey !== todayKey()} onClick={regenerate}>Regenerate</Button></span>
                    </Tooltip>
                    <Button variant="text" startIcon={<RefreshIcon />} onClick={() => { loadDays(); loadDay(dayKey); }}>Refresh</Button>
                </Stack>

                {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
                {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Edits & rendered images are stored on an isolated studio copy — the live app digest is unaffected. Regenerate is available for today only (content is pulled from current match data).
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : cards.length === 0 ? (
                    <Alert severity="info">No digest for {dayKey}. Pick another day or Regenerate today.</Alert>
                ) : (
                    cards.map((c) => <CardEditor key={c.id} dayKey={dayKey} card={c} onSaved={onCardSaved} />)
                )}
                    </Box>
                </div>
            </div>
        </Component>
    );
}
